import { Link, useParams } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { ArrowLeft } from 'lucide-react';

import { useAdminCustomerDetail } from '@/lib/api/adminHooks';
import type { CustomerOrderRow, CustomerSubscriptionRow } from '@/types/admin';

export function CustomerDetailScreen() {
  const { customerId } = useParams<{ customerId: string }>();
  const { data, isLoading, isError } = useAdminCustomerDetail(customerId);

  if (!customerId) {
    return <p className="text-ink-muted">Missing customer id.</p>;
  }

  return (
    <div className="flex flex-col gap-24">
      <div>
        <Link
          to="/admin/subscriptions"
          className="flex items-center gap-6 type-label-sm text-ink-muted hover:text-ink"
        >
          <ArrowLeft size={14} strokeWidth={1.5} /> Back to subscriptions
        </Link>
      </div>

      {isLoading && (
        <div className="p-16 border border-line rounded-lg bg-raised type-label-md text-ink-muted">
          Loading customer…
        </div>
      )}

      {isError && (
        <div className="p-16 border border-line rounded-lg bg-raised">
          Customer not found or failed to load.
        </div>
      )}

      {data && (
        <>
          <div className="flex flex-col gap-6">
            <h1 className="type-heading-xl flex items-center gap-10">
              <span>{data.email || 'Unknown email'}</span>
              {data.subscriptions.some((s) => s.is_internal) && (
                <span
                  className="inline-block px-8 py-2 rounded text-11 uppercase tracking-wide bg-raised text-ink-muted border border-line"
                  title="Internal/test account — excluded from all aggregate metrics"
                >
                  Test
                </span>
              )}
            </h1>
            <p className="type-label-sm text-ink-muted">
              CP customer <code>{data.customer_cp_id}</code>
              {data.first_subscription_at
                ? ` · first subscribed ${format(parseISO(data.first_subscription_at), 'MMM d, yyyy')}`
                : ''}
              {` · lifetime paid ${formatCents(data.lifetime_paid_cents)}`}
            </p>
          </div>

          <section className="border border-line rounded-lg bg-raised overflow-hidden">
            <div className="p-16 border-b border-line">
              <h2 className="type-heading-md">Subscriptions</h2>
              <p className="type-label-sm text-ink-muted mt-4">
                Each row's "Paid renewals" counts only captured
                subscription_cycle orders — the honest paid-renewal signal.
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full type-label-sm">
                <thead className="bg-sunken border-b border-line text-left text-ink-muted">
                  <tr>
                    <Th>Product</Th>
                    <Th>Status</Th>
                    <Th align="right">CP cycle</Th>
                    <Th align="right">Paid renewals</Th>
                    <Th>Created</Th>
                    <Th>Next cycle</Th>
                    <Th>Cancelled</Th>
                  </tr>
                </thead>
                <tbody>
                  {data.subscriptions.map((s) => (
                    <SubRow key={s.cp_id} s={s} />
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="border border-line rounded-lg bg-raised overflow-hidden">
            <div className="p-16 border-b border-line">
              <h2 className="type-heading-md">Orders</h2>
              <p className="type-label-sm text-ink-muted mt-4">
                Newest-first, most recent 200.
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full type-label-sm">
                <thead className="bg-sunken border-b border-line text-left text-ink-muted">
                  <tr>
                    <Th>Date</Th>
                    <Th>Product</Th>
                    <Th>Source</Th>
                    <Th align="right">Cycle</Th>
                    <Th>Payment</Th>
                    <Th align="right">Amount</Th>
                    <Th>Stripe PI</Th>
                  </tr>
                </thead>
                <tbody>
                  {data.orders.map((o) => (
                    <OrderRow key={o.cp_id} o={o} />
                  ))}
                  {data.orders.length === 0 && (
                    <tr>
                      <td colSpan={7} className="p-16 text-center text-ink-muted">
                        No orders for this customer.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function SubRow({ s }: { s: CustomerSubscriptionRow }) {
  return (
    <tr className="border-t border-line-subtle">
      <Td>{s.product_label || '—'}</Td>
      <Td>{s.status || '—'}</Td>
      <Td align="right">{s.current_cycle ?? '—'}</Td>
      <Td align="right">
        <span className="font-medium">{s.paid_renewals}</span>
      </Td>
      <Td>{s.created_at ? format(parseISO(s.created_at), 'MMM d, yyyy') : '—'}</Td>
      <Td>
        {s.next_cycle_date
          ? format(parseISO(s.next_cycle_date), 'MMM d, yyyy')
          : '—'}
      </Td>
      <Td>
        {s.cancelled_at
          ? format(parseISO(s.cancelled_at), 'MMM d, yyyy')
          : '—'}
      </Td>
    </tr>
  );
}

function OrderRow({ o }: { o: CustomerOrderRow }) {
  return (
    <tr className="border-t border-line-subtle">
      <Td>
        {o.created_at ? format(parseISO(o.created_at), 'MMM d, yyyy') : '—'}
      </Td>
      <Td>{o.product_label || '—'}</Td>
      <Td>{o.source || '—'}</Td>
      <Td align="right">{o.sub_current_cycle ?? '—'}</Td>
      <Td>
        <span
          className={
            o.payment_status === 'paid'
              ? 'text-ink'
              : 'text-danger'
          }
        >
          {o.payment_status || '—'}
        </span>
      </Td>
      <Td align="right">{o.amount_cents != null ? formatCents(o.amount_cents) : '—'}</Td>
      <Td>
        {o.stripe_payment_id ? (
          <code className="text-11 text-ink-muted">
            {o.stripe_payment_id.slice(0, 20)}
          </code>
        ) : (
          '—'
        )}
      </Td>
    </tr>
  );
}

function Th({ children, align }: { children: React.ReactNode; align?: 'right' }) {
  return (
    <th
      className={`px-12 py-8 font-medium ${
        align === 'right' ? 'text-right' : ''
      }`}
    >
      {children}
    </th>
  );
}

function Td({ children, align }: { children: React.ReactNode; align?: 'right' }) {
  return (
    <td className={`px-12 py-8 ${align === 'right' ? 'text-right' : ''}`}>
      {children}
    </td>
  );
}

function formatCents(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  }).format(cents / 100);
}
